import { useEffect, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { useSettings } from "@/lib/settings";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Plus, Trash, ArrowLeft, ArrowUp, ArrowDown, LinkSimple, CheckSquare, EnvelopeSimple, Calendar } from "@phosphor-icons/react";
import { toast } from "sonner";
import SubmissionsList from "@/components/SubmissionsList";

const FIELD_TYPES = [
  { v: "short_text", l: "Short text" }, { v: "long_text", l: "Long text" },
  { v: "select", l: "Dropdown (single)" }, { v: "multi_select", l: "Multi-select" },
  { v: "date", l: "Date" }, { v: "email", l: "Email" }, { v: "checkbox", l: "Yes / No" },
];
const ISSUE_TYPES = ["Onboarding", "Offboarding", "General Issue"];

// Map between the server-side `target_teams` array and the single-value
// radio shown to admins. "Both" is order-independent so we accept either
// ["it","systems"] or ["systems","it"]; we normalise on save.
const queueChoice = (teams) => {
  const arr = Array.isArray(teams) ? teams : [];
  const hasIt = arr.includes("it");
  const hasSys = arr.includes("systems");
  if (hasIt && hasSys) return "both";
  if (hasIt) return "it";
  return "systems"; // default (empty array or missing)
};
const queueChoiceToTeams = (choice) => {
  if (choice === "it") return ["it"];
  if (choice === "both") return ["systems", "it"];
  return ["systems"];
};

const newField = () => ({
  id: `fld_${Math.random().toString(36).slice(2, 10)}`,
  field_type: "short_text", label: "", required: false, help_text: "", placeholder: "", options: [], order: 0, generate_checklist: false, checklist_item_template: "", add_to_thread: false, fillable_later: false,
});

export default function FormBuilder() {
  const { id } = useParams();
  const { settings } = useSettings();
  const [f, setF] = useState(null);
  const [saving, setSaving] = useState(false);
  const [staffUsers, setStaffUsers] = useState([]);

  useEffect(() => { api.get(`/forms/${id}`).then((r) => setF(r.data)); }, [id]);
  useEffect(() => {
    api.get("/users").then((r) => setStaffUsers(
      (r.data || []).filter((u) => u.is_active !== false && (u.role === "admin" || u.role === "agent"))
    ));
  }, []);

  if (!f) return <div className="text-slate-500 text-sm p-6">Loading…</div>;

  const patch = (updates) => setF((cur) => ({ ...cur, ...updates }));
  const updField = (idx, u) => setF((cur) => ({ ...cur, fields: cur.fields.map((x, i) => i === idx ? { ...x, ...u } : x) }));
  const removeField = (idx) => setF((cur) => ({ ...cur, fields: cur.fields.filter((_, i) => i !== idx) }));
  const move = (idx, dir) => {
    setF((cur) => {
      const arr = [...cur.fields];
      const j = idx + dir;
      if (j < 0 || j >= arr.length) return cur;
      [arr[idx], arr[j]] = [arr[j], arr[idx]];
      return { ...cur, fields: arr };
    });
  };
  const addField = () => setF((cur) => ({ ...cur, fields: [...cur.fields, newField()] }));

  // Validation — the form can't be saved if approval is on but no email
  // field has been picked as the approver.
  const approverIssue = (() => {
    if (!f?.requires_manager_approval) return null;
    const emailFields = (f?.fields || []).filter((x) => x.field_type === "email");
    if (emailFields.length === 0) return "Add an Email-type field, then pick it as the approver, before saving.";
    if (!emailFields.some((x) => x.is_manager_approval_email)) return "Select which email field is the approver — approval is enabled but no field is chosen.";
    return null;
  })();

  const save = async () => {
    if (approverIssue) { toast.error(approverIssue); return; }
    setSaving(true);
    try {
      const r = await api.patch(`/forms/${f.id}`, {
        title: f.title, description: f.description, issue_type: f.issue_type,
        system: f.system || null, is_active: f.is_active,
        // §form-builder — sanitise per-field options on save (trim +
        // drop empties). The editor keeps the raw multi-line string
        // during typing so newlines / spaces aren't eaten mid-edit.
        fields: (f.fields || []).map((fld) => ({
          ...fld,
          options: Array.isArray(fld.options)
            ? fld.options.map((s) => (s || "").trim()).filter(Boolean)
            : fld.options,
        })),
        ticket_subject: (f.ticket_subject || "").trim() || null,
        checklist_template: (f.checklist_template || []).map((s) => (s || "").trim()).filter(Boolean),
        notify_user_ids: f.notify_user_ids || [],
        notify_external_emails: (f.notify_external_emails || []).map((s) => (s || "").trim().toLowerCase()).filter((s) => s.includes("@")),
        requires_manager_approval: !!f.requires_manager_approval,
        key_date_field_id: f.key_date_field_id || null,
        key_date_label: (f.key_date_label || "").trim() || null,
        subject_name_field_id: f.subject_name_field_id || null,
        lead_time_days: f.lead_time_days === "" || f.lead_time_days == null ? null : Number(f.lead_time_days),
        target_teams: queueChoiceToTeams(queueChoice(f.target_teams)),
      });
      setF(r.data);
      toast.success("Saved");
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  const shareUrl = `${window.location.origin}/forms/${f.slug}`;
  const copyLink = () => { navigator.clipboard.writeText(shareUrl); toast.success("Link copied"); };

  return (
    <div className="space-y-4">
      <Link to="/admin/forms" className="text-xs text-slate-500 hover:text-slate-900 flex items-center gap-1"><ArrowLeft size={12} /> All forms</Link>
      <div className="flex items-center justify-between">
        <h1 className="font-heading text-2xl font-bold tracking-tight">{f.title || "Untitled form"}</h1>
        <div className="flex items-center gap-2">
          <button onClick={copyLink} className="text-xs text-slate-500 hover:text-[#4f4c90] flex items-center gap-1 border border-slate-200 rounded-md px-2 py-1.5" data-testid="copy-form-link"><LinkSimple size={12} /> Copy link</button>
          <label className="flex items-center gap-2 text-xs text-slate-600">
            <Switch checked={f.is_active} onCheckedChange={(v) => patch({ is_active: v })} data-testid="form-active-switch" /> Active
          </label>
          <Button
            onClick={save}
            disabled={saving || !!approverIssue}
            title={approverIssue || ""}
            className="bg-[#4f4c90] hover:bg-[#5F5CA5] h-9"
            data-testid="form-save"
          >
            {saving ? "Saving…" : "Save"}
          </Button>
        </div>
      </div>

      <div className="hairline rounded-lg bg-white p-4 space-y-3">
        <div>
          <Label className="text-xs">Title</Label>
          <Input value={f.title} onChange={(e) => patch({ title: e.target.value })} className="mt-1 bg-white border-slate-200" />
        </div>
        <div>
          <Label className="text-xs">Description (optional)</Label>
          <Textarea value={f.description || ""} onChange={(e) => patch({ description: e.target.value })} className="mt-1 bg-white border-slate-200 min-h-16" />
        </div>
        <div>
          <Label className="text-xs">Ticket subject</Label>
          <SubjectTemplateEditor
            value={f.ticket_subject || ""}
            fields={f.fields || []}
            onChange={(v) => patch({ ticket_subject: v })}
            formTitle={f.title}
          />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-xs">Issue type</Label>
            <Select value={f.issue_type} onValueChange={(v) => patch({ issue_type: v })}>
              <SelectTrigger className="mt-1 bg-white border-slate-200"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white border-slate-200">
                {ISSUE_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">System (optional)</Label>
            <Select value={f.system || "__none__"} onValueChange={(v) => patch({ system: v === "__none__" ? null : v })}>
              <SelectTrigger className="mt-1 bg-white border-slate-200"><SelectValue placeholder="—" /></SelectTrigger>
              <SelectContent className="bg-white border-slate-200">
                <SelectItem value="__none__">— none —</SelectItem>
                {(settings?.systems || []).map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
        <div>
          <Label className="text-xs">Which queue does this form go to?</Label>
          <Select
            value={queueChoice(f.target_teams)}
            onValueChange={(v) => patch({ target_teams: queueChoiceToTeams(v) })}
          >
            <SelectTrigger className="mt-1 bg-white border-slate-200" data-testid="form-target-queue"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-white border-slate-200">
              <SelectItem value="systems">Business Systems only</SelectItem>
              <SelectItem value="it">IT only</SelectItem>
              <SelectItem value="both">Both queues (Systems + IT)</SelectItem>
            </SelectContent>
          </Select>
          <p className="text-[11px] text-slate-500 mt-1">
            Every submission creates a ticket in the selected queue(s). "Both" is useful for cross-team requests like onboarding so neither team misses it.
          </p>
        </div>
        <div className="text-[11px] text-slate-500 font-mono border-t border-slate-100 pt-2">
          Share URL: <a href={shareUrl} target="_blank" rel="noreferrer" className="text-[#4f4c90] hover:underline">{shareUrl}</a>
        </div>
      </div>

      {/* Manager approval — moved to the top so the setup is visible before you scroll */}
      <div className="hairline rounded-lg bg-white p-4 space-y-3">
        <div className="flex items-center gap-2">
          <CheckSquare size={16} weight="duotone" className="text-[#4f4c90]" />
          <div className="text-[11px] uppercase tracking-wider text-slate-500 font-medium">Manager approval</div>
        </div>
        <label className="flex items-center gap-2 text-sm cursor-pointer" data-testid="requires-manager-approval-toggle">
          <input
            type="checkbox"
            checked={!!f.requires_manager_approval}
            onChange={(e) => patch({ requires_manager_approval: e.target.checked })}
          />
          <span className="text-slate-800">Requires manager approval</span>
        </label>
        {f.requires_manager_approval && (() => {
          const emailFields = (f.fields || []).filter((x) => x.field_type === "email");
          const currentFlag = emailFields.find((x) => x.is_manager_approval_email);
          const setApproverField = (fieldId) => {
            const next = (f.fields || []).map((x) => ({
              ...x,
              is_manager_approval_email: x.field_type === "email" && x.id === fieldId,
            }));
            patch({ fields: next });
          };
          return (
            <div className="rounded-md border border-slate-200 bg-slate-50/60 p-3 space-y-2" data-testid="approver-field-picker">
              <div className="text-[11px] text-slate-500 leading-snug">
                Tickets from this form are held in a <b>Pending approval</b> state and an <b>Approve / Reject</b> email is sent to the manager address the submitter enters. Rejected requests are closed automatically.
              </div>
              <Label className="text-xs">Which field holds the manager&apos;s email?</Label>
              {emailFields.length === 0 ? (
                <div className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
                  You haven&apos;t added any <b>Email</b> fields to this form yet. Add one below (with type <b>Email</b>) — that&apos;s the field the submitter will fill in with the manager&apos;s address.
                </div>
              ) : (
                <>
                  <Select
                    value={currentFlag?.id || "__none__"}
                    onValueChange={(v) => setApproverField(v === "__none__" ? null : v)}
                  >
                    <SelectTrigger className="bg-white border-slate-200" data-testid="approver-field-select">
                      <SelectValue placeholder="— select which email field is the approver —" />
                    </SelectTrigger>
                    <SelectContent className="bg-white border-slate-200">
                      <SelectItem value="__none__">— none —</SelectItem>
                      {emailFields.map((x) => (
                        <SelectItem key={x.id} value={x.id}>
                          {x.label || "(unlabelled email field)"}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {currentFlag ? (
                    <div className="text-[11px] text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-md px-3 py-2">
                      Approval email will be sent to whatever the submitter enters in <b>{currentFlag.label}</b>.
                    </div>
                  ) : (
                    <div className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2" data-testid="approver-field-required-warning">
                      Select which email field is the approver — otherwise this form can&apos;t be saved.
                    </div>
                  )}
                </>
              )}
            </div>
          );
        })()}
      </div>

      <div className="hairline rounded-lg bg-white p-4 space-y-3">
        <div className="text-[11px] uppercase tracking-wider text-slate-500 font-medium">Custom fields</div>
        <div className="text-[11px] text-slate-500 font-mono bg-slate-50 border border-slate-200 rounded-md px-3 py-2">
          Every form automatically starts with a required <b>Brief summary</b> field (maps to Subject) and a <b>Region</b> selector. You don&apos;t need to add those.
        </div>
        {f.fields.map((fld, idx) => (
          <div key={fld.id} className="border border-slate-200 rounded-md p-3 space-y-2 bg-slate-50/50" data-testid={`field-${idx}`}>
            <div className="flex items-center gap-2">
              <Select value={fld.field_type} onValueChange={(v) => updField(idx, { field_type: v })}>
                <SelectTrigger className="w-40 bg-white border-slate-200 h-8"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white border-slate-200">
                  {FIELD_TYPES.map((t) => <SelectItem key={t.v} value={t.v}>{t.l}</SelectItem>)}
                </SelectContent>
              </Select>
              <Input placeholder="Field label" value={fld.label} onChange={(e) => updField(idx, { label: e.target.value })} className="flex-1 h-8 bg-white border-slate-200" />
              <label className="flex items-center gap-1 text-xs text-slate-600"><Switch checked={fld.required} onCheckedChange={(v) => updField(idx, { required: v })} /> Required</label>
              <button onClick={() => move(idx, -1)} className="p-1 text-slate-400 hover:text-slate-800" title="Move up"><ArrowUp size={12} /></button>
              <button onClick={() => move(idx, 1)} className="p-1 text-slate-400 hover:text-slate-800" title="Move down"><ArrowDown size={12} /></button>
              <button onClick={() => removeField(idx)} className="p-1 text-red-500 hover:text-red-600" title="Delete"><Trash size={12} /></button>
            </div>
            <Input placeholder="Placeholder / help text" value={fld.help_text || ""} onChange={(e) => updField(idx, { help_text: e.target.value })} className="h-8 bg-white border-slate-200 text-xs" />
            {/* Stage 4 — fillable_later toggle. Non-checkbox fields only. */}
            {fld.field_type !== "checkbox" && (
              <label className="flex items-center gap-2 text-[11px] text-slate-600 cursor-pointer pl-2" data-testid={`fillable-later-toggle-${idx}`}>
                <input
                  type="checkbox"
                  checked={!!fld.fillable_later}
                  onChange={(e) => updField(idx, { fillable_later: e.target.checked })}
                />
                <span className="text-slate-800">Editable after submission</span>
                <span className="text-slate-400">— submitters can leave this blank on the form and edit it from the portal any time before the ticket closes</span>
              </label>
            )}            {fld.field_type === "email" && (
              <div className="rounded-md border border-slate-200 bg-slate-50/60 p-2 space-y-2">
                <label className="flex items-center gap-2 text-xs cursor-pointer" data-testid={`add-to-thread-toggle-${idx}`}>
                  <input
                    type="checkbox"
                    checked={!!fld.add_to_thread}
                    onChange={(e) => updField(idx, { add_to_thread: e.target.checked })}
                  />
                  <span className="text-slate-800">Add to ticket thread (CC on emails)</span>
                </label>
                <div className="text-[11px] text-slate-500 leading-snug pl-6 -mt-1">
                  When enabled, the address submitted here is CC&apos;d on the ticket&apos;s email thread — they receive replies and their replies show up as thread messages. Use this for e.g. line manager or secondary contact.
                </div>
                {f?.requires_manager_approval && fld.is_manager_approval_email && (
                  <div className="text-[11px] text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-md px-2 py-1 flex items-center gap-1">
                    <CheckSquare size={11} weight="fill" /> Approver email — set in the Manager approval section above
                  </div>
                )}
              </div>
            )}
            {(fld.field_type === "select" || fld.field_type === "multi_select") && (
              <div className="space-y-2">
                <div>
                  <Label className="text-[10px] uppercase text-slate-500 tracking-wider">Options (one per line)</Label>
                  <Textarea
                    value={(fld.options || []).join("\n")}
                    onChange={(e) => updField(idx, {
                      // §form-builder — keep raw text intact during typing.
                      // Trimming per keystroke ate leading spaces and killed
                      // the trailing newline (Enter appeared to do nothing).
                      // We only sanitise on save (see `saveForm` → each field
                      // filters empty options before POST).
                      options: e.target.value.split("\n"),
                    })}
                    className="mt-1 bg-white border-slate-200 min-h-16 text-xs font-mono"
                  />
                </div>
                <div className="rounded-md border border-slate-200 bg-slate-50/60 p-2 space-y-2">
                  <label className="flex items-center gap-2 text-xs cursor-pointer" data-testid={`gen-checklist-toggle-${idx}`}>
                    <input
                      type="checkbox"
                      checked={!!fld.generate_checklist}
                      onChange={(e) => updField(idx, { generate_checklist: e.target.checked })}
                    />
                    <span className="text-slate-800">Generate checklist items from selections</span>
                  </label>
                  <div className="text-[11px] text-slate-500 leading-snug pl-6">
                    On submit, every option the submitter picks becomes an unchecked checklist item on the resulting ticket. Adds to the static template below.
                  </div>
                  {fld.generate_checklist && (
                    <div className="pl-6">
                      <Label className="text-[10px] uppercase text-slate-500 tracking-wider">Label template (optional)</Label>
                      <Input
                        placeholder="e.g. Provision access: {option}"
                        value={fld.checklist_item_template || ""}
                        onChange={(e) => updField(idx, { checklist_item_template: e.target.value })}
                        className="h-8 bg-white border-slate-200 text-xs mt-1"
                        data-testid={`checklist-item-template-${idx}`}
                      />
                      <div className="text-[10px] text-slate-500 mt-1 font-mono">
                        Use <code>{"{option}"}</code> as the placeholder — blank uses the option&apos;s raw name.
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        ))}
        <Button variant="outline" onClick={addField} className="w-full border-dashed border-slate-300" data-testid="add-field-btn"><Plus size={14} /> Add field</Button>
      </div>

      {/* Feature A — Checklist Template */}
      <div className="hairline rounded-lg bg-white p-4 space-y-3">
        <div className="flex items-center gap-2">
          <CheckSquare size={16} weight="duotone" className="text-[#4f4c90]" />
          <div className="text-[11px] uppercase tracking-wider text-slate-500 font-medium">Checklist template</div>
        </div>
        <div className="text-[11px] text-slate-500 font-mono bg-slate-50 border border-slate-200 rounded-md px-3 py-2">
          Items listed here will be auto-added as an unchecked to-do list on every ticket created from this form.
        </div>
        <div className="space-y-2">
          {(f.checklist_template || []).map((item, idx) => {
            // Stable key: derive from content + position hash. Not perfect,
            // but paired with controlled Input value prop, it prevents the
            // "focus jumps mid-edit" and "swap-loses-cursor" bugs from
            // pure-index keys during reorder.
            const stableKey = `tpl-${idx}-${(item || "").slice(0, 24)}`;
            return (
            <div key={stableKey} className="flex items-center gap-2" data-testid={`checklist-tpl-${idx}`}>
              <span className="text-[10px] font-mono text-slate-400 w-4 text-right">{idx + 1}.</span>
              <Input
                value={item}
                onChange={(e) => {
                  const arr = [...(f.checklist_template || [])];
                  arr[idx] = e.target.value;
                  patch({ checklist_template: arr });
                }}
                placeholder="Checklist item…"
                className="h-8 bg-white border-slate-200 flex-1"
              />
              <button
                onClick={() => {
                  const arr = [...(f.checklist_template || [])];
                  if (idx > 0) { [arr[idx - 1], arr[idx]] = [arr[idx], arr[idx - 1]]; patch({ checklist_template: arr }); }
                }}
                disabled={idx === 0}
                className="p-1 text-slate-400 hover:text-slate-800 disabled:opacity-30"
              ><ArrowUp size={12} /></button>
              <button
                onClick={() => {
                  const arr = [...(f.checklist_template || [])];
                  if (idx < arr.length - 1) { [arr[idx + 1], arr[idx]] = [arr[idx], arr[idx + 1]]; patch({ checklist_template: arr }); }
                }}
                disabled={idx === (f.checklist_template || []).length - 1}
                className="p-1 text-slate-400 hover:text-slate-800 disabled:opacity-30"
              ><ArrowDown size={12} /></button>
              <button
                onClick={() => patch({ checklist_template: (f.checklist_template || []).filter((_, i) => i !== idx) })}
                className="p-1 text-red-500 hover:text-red-600"
                data-testid={`checklist-tpl-delete-${idx}`}
              ><Trash size={12} /></button>
            </div>
            );
          })}
          {(!f.checklist_template || f.checklist_template.length === 0) && (
            <div className="text-xs text-slate-500 italic">No checklist items yet.</div>
          )}
        </div>
        <Button
          variant="outline"
          onClick={() => patch({ checklist_template: [...(f.checklist_template || []), ""] })}
          className="w-full border-dashed border-slate-300"
          data-testid="checklist-tpl-add"
        ><Plus size={14} /> Add checklist item</Button>
      </div>

      {/* Feature C — Notification recipients */}
      <div className="hairline rounded-lg bg-white p-4 space-y-3">
        <div className="flex items-center gap-2">
          <EnvelopeSimple size={16} weight="duotone" className="text-[#4f4c90]" />
          <div className="text-[11px] uppercase tracking-wider text-slate-500 font-medium">Notification recipients</div>
        </div>
        <div className="text-[11px] text-slate-500 font-mono bg-slate-50 border border-slate-200 rounded-md px-3 py-2">
          These users will get an email with the full submission any time this form is submitted.
        </div>
        <div className="space-y-1">
          {staffUsers.map((u) => {
            const selected = (f.notify_user_ids || []).includes(u.id);
            return (
              <label
                key={u.id}
                className="flex items-center gap-2 py-1 px-2 rounded hover:bg-slate-50 cursor-pointer"
                data-testid={`notify-user-${u.email}`}
              >
                <Checkbox
                  checked={selected}
                  onCheckedChange={(v) => {
                    const cur = new Set(f.notify_user_ids || []);
                    if (v) cur.add(u.id); else cur.delete(u.id);
                    patch({ notify_user_ids: [...cur] });
                  }}
                />
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-slate-800 truncate">{u.name}</div>
                  <div className="text-[11px] text-slate-500 font-mono truncate">{u.email}</div>
                </div>
                <span className="chip capitalize text-[10px] !py-0.5">{u.role}</span>
              </label>
            );
          })}
          {staffUsers.length === 0 && (
            <div className="text-xs text-slate-500 italic">No staff users found.</div>
          )}
        </div>

        <div className="pt-2 border-t border-slate-100">
          <Label className="text-xs">External email addresses (not app users)</Label>
          <div className="text-[11px] text-slate-500 font-mono mt-1 mb-2">
            One per line. Anyone here gets the same notification email — great for stakeholders who don&apos;t have an account in this app.
          </div>
          <Textarea
            value={(f.notify_external_emails || []).join("\n")}
            onChange={(e) => patch({ notify_external_emails: e.target.value.split("\n").map((s) => s.trim()).filter(Boolean) })}
            placeholder="stakeholder@example.com&#10;ops@example.com"
            className="bg-white border-slate-200 min-h-20 font-mono text-[12px]"
            data-testid="notify-external-emails"
          />
        </div>
      </div>

      {/* Feature E — Key Date field mapping */}
      <div className="hairline rounded-lg bg-white p-4 space-y-3">
        <div className="flex items-center gap-2">
          <Calendar size={16} weight="duotone" className="text-[#4f4c90]" />
          <div className="text-[11px] uppercase tracking-wider text-slate-500 font-medium">Key Date + Subject person</div>
        </div>
        <div className="text-[11px] text-slate-500 font-mono bg-slate-50 border border-slate-200 rounded-md px-3 py-2">
          Optionally map one of this form&apos;s <b>Date</b> fields onto the Key Date callout, and one text field onto the <b>Subject person</b> (e.g. the new starter&apos;s name, so it shows on the calendar instead of the submitter&apos;s).
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-xs">Date field to map</Label>
            <Select
              value={f.key_date_field_id || "__none__"}
              onValueChange={(v) => patch({ key_date_field_id: v === "__none__" ? null : v })}
            >
              <SelectTrigger className="mt-1 bg-white border-slate-200" data-testid="key-date-field-select"><SelectValue placeholder="— none —" /></SelectTrigger>
              <SelectContent className="bg-white border-slate-200">
                <SelectItem value="__none__">— none —</SelectItem>
                {(f.fields || []).filter((x) => x.field_type === "date").map((x) => (
                  <SelectItem key={x.id} value={x.id}>{x.label || "(unlabelled date field)"}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {(f.fields || []).filter((x) => x.field_type === "date").length === 0 && (
              <div className="text-[11px] text-slate-500 italic mt-1">Add a Date field above to enable mapping.</div>
            )}
          </div>
          <div>
            <Label className="text-xs">Label shown on the ticket (optional)</Label>
            <Input
              value={f.key_date_label || ""}
              onChange={(e) => patch({ key_date_label: e.target.value })}
              placeholder="e.g. Start Date"
              className="mt-1 bg-white border-slate-200"
              data-testid="key-date-label-input"
            />
          </div>
          <div>
            <Label className="text-xs">Subject person field (Stage 4)</Label>
            <Select
              value={f.subject_name_field_id || "__none__"}
              onValueChange={(v) => patch({ subject_name_field_id: v === "__none__" ? null : v })}
            >
              <SelectTrigger className="mt-1 bg-white border-slate-200" data-testid="subject-name-field-select"><SelectValue placeholder="— none —" /></SelectTrigger>
              <SelectContent className="bg-white border-slate-200">
                <SelectItem value="__none__">— none —</SelectItem>
                {(f.fields || []).filter((x) => x.field_type === "short_text").map((x) => (
                  <SelectItem key={x.id} value={x.id}>{x.label || "(unlabelled field)"}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="text-[11px] text-slate-500 italic mt-1">Populates <code>ticket.subject_name</code>. Use for onboarding forms so the new starter&apos;s name appears on the calendar pill.</div>
          </div>
          {(f.issue_type === "Onboarding" || f.issue_type === "Offboarding") && (
            <div>
              <Label className="text-xs">On-time lead time (business days)</Label>
              <Input
                type="number"
                min={0}
                max={60}
                value={f.lead_time_days ?? 5}
                onChange={(e) => {
                  const raw = e.target.value;
                  if (raw === "") { patch({ lead_time_days: null }); return; }
                  const n = Number.parseInt(raw, 10);
                  patch({ lead_time_days: Number.isFinite(n) ? Math.max(0, Math.min(60, n)) : null });
                }}
                placeholder="5"
                className="mt-1 bg-white border-slate-200"
                data-testid="lead-time-days-input"
              />
              <div className="text-[11px] text-slate-500 italic mt-1">
                Ticket must be resolved this many business days <b>before</b> the mapped date to count as on-time. Replaces SLA for {f.issue_type} tickets. Defaults to 5.
              </div>
            </div>
          )}
        </div>
      </div>

      <SubmissionsList
        apiClient={api}
        fetchUrl={`/forms/${f.id}/submissions`}
        ticketLinkPrefix="/tickets"
        emptyLabel="No submissions yet — share the public link to start collecting."
        testIdPrefix="form-submissions-section"
      />
    </div>
  );
}


/**
 * SubjectTemplateEditor — the "Ticket subject" input on the form
 * builder, with quick-insert chips for each field in the form so
 * admins never have to type raw {field_id} tokens.
 *
 * Empty template → the ticket will fall back to `form.title`.
 * Placeholder-free string → static subject for every submission.
 * String with `{field_id}` tokens → resolved at submit time and
 * re-rendered on every Stage 4 detail update.
 */
function SubjectTemplateEditor({ value, fields, onChange, formTitle }) {
  const inputRef = useRef(null);
  const [preview, setPreview] = useState("");

  useEffect(() => {
    // Render a fake preview using each field's label as the "answer".
    // Gives admins a live sense of what the ticket subject will look like.
    if (!value) { setPreview(""); return; }
    let out = value;
    const byId = Object.fromEntries((fields || []).map((f) => [f.id, f]));
    out = out.replace(/\{([A-Za-z0-9_\-]+)\}/g, (m, fid) => {
      const f = byId[fid];
      if (!f) return m;
      // Use label as a stand-in — real answers resolve at submit time.
      return `«${f.label || fid}»`;
    });
    setPreview(out.replace(/\s+/g, " ").trim());
  }, [value, fields]);

  const insertToken = (fieldId) => {
    const el = inputRef.current;
    const token = `{${fieldId}}`;
    if (!el) {
      onChange(((value || "") + " " + token).trim());
      return;
    }
    const start = el.selectionStart ?? value.length;
    const end = el.selectionEnd ?? value.length;
    const next = (value || "").slice(0, start) + token + (value || "").slice(end);
    onChange(next);
    // Restore cursor after the inserted token.
    requestAnimationFrame(() => {
      el.focus();
      const pos = start + token.length;
      try { el.setSelectionRange(pos, pos); } catch { /* ignore */ }
    });
  };

  const hasTokens = /\{[A-Za-z0-9_\-]+\}/.test(value || "");
  const fieldOptions = (fields || []).filter((f) => f.field_type !== "checkbox" && f.label);

  return (
    <div className="mt-1 space-y-2">
      <Input
        ref={inputRef}
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        className="bg-white border-slate-200 font-mono text-sm"
        placeholder={formTitle ? `Defaults to "${formTitle}" when blank` : "e.g. New Start: {full_name} — {start_date}"}
        data-testid="form-ticket-subject"
      />
      {fieldOptions.length > 0 && (
        <div className="flex items-start gap-2 flex-wrap">
          <div className="text-[11px] text-slate-500 font-mono pt-1.5 shrink-0">Insert field:</div>
          <div className="flex flex-wrap gap-1.5">
            {fieldOptions.map((f) => (
              <button
                key={f.id}
                type="button"
                onClick={() => insertToken(f.id)}
                className="text-[11px] px-2 py-0.5 rounded-md border border-slate-200 bg-white hover:bg-slate-50 hover:border-[#4f4c90]/40 text-slate-700 hover:text-[#4f4c90] transition-colors"
                data-testid={`insert-field-${f.id}`}
                title={`Inserts {${f.id}}`}
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>
      )}
      {hasTokens ? (
        <div className="text-[11px] text-slate-500 font-mono bg-slate-50 border border-slate-200 rounded-md px-3 py-2">
          <div className="uppercase tracking-wider text-slate-400 mb-0.5">Preview</div>
          <div className="text-slate-800 not-italic">{preview || "—"}</div>
          <div className="text-slate-400 mt-1 italic">Field answers replace the labels at submission time. Empty / TBC values render as “TBC”. The “[Awaiting details]” prefix is skipped when a template is in use — the amber chip below the title already signals that state.</div>
        </div>
      ) : (
        <div className="text-[11px] text-slate-500 font-mono">
          Static subject applied to every ticket. Click a field chip above to compose a dynamic title (e.g. <span className="text-slate-800">“New Start: {'{full_name}'} — {'{start_date}'}”</span>).
        </div>
      )}
    </div>
  );
}
